import { ChevronDown } from "lucide-react";
import * as React from "react";

import { Button } from "src/core/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "src/core/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "src/core/components/ui/popover";
import { IRiskOption, ISelectOption } from "src/core/constants/options";
import { RISK_LEVEL_CLASSES } from "src/core/constants/risk";
import { cn } from "src/core/lib/utils";

const isRiskOption = (option: ISelectOption | IRiskOption): option is IRiskOption => {
  return "risk" in option;
};

type ComboboxProps = {
  list: ISelectOption[] | IRiskOption[];
  id: string;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  popoverClassName?: string;
  renderOption?: (option: ISelectOption) => React.ReactNode;
  withBadge?: boolean;
  error?: boolean;
  disabled?: boolean;
  isInModal?: boolean;
};

const normalizeString = (str: string): string => {
  return str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};

export const Combobox: React.FC<ComboboxProps> = ({
  list,
  id,
  value,
  onChange,
  placeholder = "Sélectionner...",
  searchPlaceholder = "Rechercher...",
  emptyMessage = "Aucun résultat trouvé.",
  className = "",
  popoverClassName,
  renderOption,
  withBadge = false,
  error = false,
  disabled = false,
  isInModal = false,
}) => {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const filteredList = React.useMemo(() => {
    if (!search) return list;
    const normalizedSearch = normalizeString(search);
    return list.filter((item) =>
      normalizeString(item.label).toLowerCase().startsWith(normalizedSearch.toLowerCase())
    );
  }, [list, search]);
  const selectedOption = list.find((option) => option.value === value);
  return (
    <Popover open={open} onOpenChange={setOpen} modal={isInModal}>
      <PopoverTrigger asChild>
        <Button
          variant="combobox"
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id}-listbox`}
          className={cn("w-full justify-between", error && "border-alert", className)}
          disabled={disabled}
        >
          {selectedOption ? (
            <div className="flex items-center gap-2 text-ellipsis overflow-hidden whitespace-nowrap">
              {withBadge && isRiskOption(selectedOption) && selectedOption.risk && (
                <div
                  className={cn(
                    "min-w-[16px] h-[16px] rounded-full",
                    RISK_LEVEL_CLASSES[selectedOption.risk]
                  )}
                />
              )}
              <span className={cn("truncate")}>{selectedOption.label}</span>
            </div>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronDown />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn("w-[var(--radix-popover-trigger-width)] p-0", popoverClassName)}
        align="start"
        side="bottom"
      >
        <Command shouldFilter={false}>
          <CommandInput placeholder={searchPlaceholder} value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {filteredList.map((element) => (
                <CommandItem
                  key={element.value}
                  value={element.value}
                  onSelect={(currentValue) => {
                    onChange?.(currentValue === value ? "" : currentValue);
                    setOpen(false);
                    setSearch("");
                  }}
                >
                  <div className="flex items-center gap-2 text-ellipsis overflow-hidden whitespace-nowrap">
                    {withBadge && isRiskOption(element) && element.risk && (
                      <div
                        className={`min-w-[16px] h-[16px] rounded-full ${
                          RISK_LEVEL_CLASSES[element.risk]
                        }`}
                      />
                    )}
                    <span className={cn("font-light truncate", disabled ? "opacity-50" : "")}>
                      {renderOption ? renderOption(element) : element.label}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
